/** Facilities / Companies - the legal entities that file, and who owns whom (ADR-0031, ADR-0212).
 *
 * One row per company: its country, its parent and since when (from the effective-dated ownership
 * edges the organisation tree reads through `org.graph`), its sites, its filing windows and what its
 * own sites add up to in the period. **There is no "owned share" column because the model holds none**
 * (the tree says so: ownership percentage does not exist, and nothing is multiplied by a share); the
 * table says when a company was held from and to instead.
 *
 * **Admin only for anything that changes it** (the shape of the company changes what every figure is
 * about): Add company, the correction a row opens, Ownership and Filing windows. Everyone else reads
 * the table and is offered no control that would 403.
 */

import { Briefcase } from "@phosphor-icons/react";
import { useState } from "react";

import { useMe } from "../../api/auth";
import { useEntities } from "../../api/queries";
import { useOrgTree } from "../../api/vendorSummary";
import { ReadFailed } from "../../components/Loaded";
import { AddImportActions } from "../../components/ui/AddImportActions";
import { Drawer } from "../../components/ui/Drawer";
import { EmptyAction } from "../../components/ui/EmptyAction";
import { EmptyState } from "../../components/ui/EmptyState";
import { FilterLine } from "../../components/ui/FilterLine";
import { EMPTY_FILTERS } from "../../components/ui/filters";
import type { FilterState } from "../../components/ui/filters";
import { GridTable } from "../../components/ui/GridTable";
import type { GridColumn } from "../../components/ui/GridTable";
import { InfoPopover } from "../../components/ui/InfoPopover";
import type { SortState } from "../../components/ui/sortRows";
import { TablePage } from "../../components/ui/TablePage";
import { Tile, TileStrip } from "../../components/ui/Tile";
import { shortDate, tonnes } from "../../lib/format";
import { EntityForm, PeriodForm, PeriodList } from "../Organisation";
import { EntityCorrection } from "../OrganisationEdit";
import { GroupSection } from "../OrganisationGroup";
import { BasisAndLimits } from "../ScreenParts";
import { OpenFigures, count, matches, ordered, usePlaceLabel } from "../tableKit";
import type { Screen } from "../useScreen";
import styles from "./Facilities.module.css";
import { useReporter } from "./useReporter";

import type { Entity } from "../../api/queries";

type Panel = "add" | "ownership" | "windows" | null;

export function CompaniesTab({ screen }: { screen: Screen }) {
  const label = usePlaceLabel();
  const isAdmin = useMe().data?.role === "admin";
  const entities = useEntities();
  const window = screen.window;
  const tree = useOrgTree(window?.period_start, window?.period_end, screen.method);
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [sort, setSort] = useState<SortState | null>(null);
  const [panel, setPanel] = useState<Panel>(null);
  const [correcting, setCorrecting] = useState<string | null>(null);
  const reporter = useReporter(() => void entities.refetch());

  const rows = entities.data ?? [];
  const names = new Map(rows.map((e) => [e.id, e.legal_name]));
  const edgeOf = new Map((tree.data?.edges ?? []).map((edge) => [edge.child_entity_id, edge]));
  const totalOf = new Map((tree.data?.entities ?? []).map((e) => [e.entity_id, e.totals.kgco2e]));
  const figuresOf = new Map((tree.data?.entities ?? []).map((e) => [e.entity_id, e.totals.figures]));
  const periods = screen.organization?.periods ?? [];
  const periodsOf = (entity: Entity) =>
    periods.filter((p) => p.legal_entity_name === entity.legal_name).map((p) => p.label);
  const companies = rows.map((e) => ({ id: e.id, name: e.legal_name }));

  const shown = ordered(
    rows.filter((e) => matches(filters.search, e.legal_name, e.jurisdiction)),
    sort,
    {
      company: (e) => e.legal_name,
      sites: (e) => e.facility_count,
    },
  );

  const columns: GridColumn<Entity>[] = [
    { id: "company", header: "Company", sortable: true, cell: (e) => e.legal_name },
    { id: "jurisdiction", header: "Country", cell: (e) => e.jurisdiction },
    {
      id: "parent",
      header: "Parent",
      cell: (e) => {
        const edge = edgeOf.get(e.id);
        return edge ? (names.get(edge.parent_entity_id) ?? "—") : "—";
      },
    },
    {
      id: "held",
      header: "Held from",
      cell: (e) => {
        const edge = edgeOf.get(e.id);
        return edge ? shortDate(edge.held_from) : "—";
      },
    },
    {
      id: "sites",
      header: "Sites",
      numeric: true,
      sortable: true,
      /* **Zero said out loud.** A company with no sites contributes no figures, and a blank beside it
         invites somebody to pick it and conclude the inventory is empty. */
      cell: (e) =>
        e.facility_count === 0 ? <span data-testid="no-sites">No sites yet</span> : count(e.facility_count),
    },
    { id: "period", header: "Reporting periods", cell: (e) => periodsOf(e).join(", ") || "—" },
    {
      id: "tco2e",
      header: "tCO2e",
      numeric: true,
      cell: (e) =>
        e.facility_count === 0 ? (
          <span data-testid="no-figures">No figures</span>
        ) : (
          <OpenFigures
            screen={screen}
            nodeKey={`company-${e.id}`}
            value={totalOf.has(e.id) && (figuresOf.get(e.id) ?? 0) > 0 ? tonnes(totalOf.get(e.id) ?? "0") : "—"}
            name={e.legal_name}
          />
        ),
    },
  ];

  const corrected = rows.find((e) => e.id === correcting);
  const closePanel = () => {
    setPanel(null);
    reporter.clear();
  };

  return (
    <>
      <TablePage
        icon={Briefcase}
        title="Facilities"
        label={label}
        actions={
          <>
            <InfoPopover infoKey="facilities.companies" />
            <AddImportActions
              role={isAdmin ? "admin" : undefined}
              addLabel="Add company"
              testId="open-add-company"
              onAdd={() => setPanel("add")}
            />
          </>
        }
        extra={
          /* Design rule 6: a role that cannot act is told who can, and is offered no control that
             would 403. One sentence, the state of the page. */
          isAdmin ? undefined : (
            <p className={styles.who} data-testid="facilities-who-can">
              An Admin sets up companies, sites and reporting periods.
            </p>
          )
        }
        tiles={
          entities.data ? (
            <TileStrip label="Companies summary">
              <Tile value={count(rows.length)} label="Companies" />
              <Tile value={count(rows.reduce((n, e) => n + e.facility_count, 0))} label="Sites" />
              <Tile value={count(periods.length)} label="Reporting periods" />
            </TileStrip>
          ) : undefined
        }
        filters={
          // With no company there is no ownership to edit and no window to set: those two controls
          // would open panels with nothing in them, so the line is drawn once a company exists.
          entities.data && rows.length === 0 ? undefined : (
            <FilterLine
              filters={[]}
              state={filters}
              onChange={setFilters}
              searchLabel="Search companies"
              resultCount={entities.data ? { shown: shown.length, total: rows.length, noun: "companies" } : undefined}
              actions={
                isAdmin ? (
                  <>
                    <button type="button" className={styles.ghost} onClick={() => setPanel("ownership")}>
                      Ownership
                    </button>
                    <button type="button" className={styles.ghost} onClick={() => setPanel("windows")}>
                      Reporting periods
                    </button>
                  </>
                ) : undefined
              }
            />
          )
        }
        table={
          entities.data ? (
            <GridTable
              caption="Companies"
              columns={columns}
              rows={shown}
              rowKey={(e) => e.id}
              rowLabel={(e) => e.legal_name}
              rowAttributes={(e) => ({ "data-entity": e.id })}
              sort={sort}
              onSortChange={setSort}
              onRowOpen={isAdmin ? (e) => setCorrecting(e.id) : undefined}
              empty={
                rows.length === 0 ? (
                  <EmptyState
                    icon={Briefcase}
                    title="No companies yet"
                    compact
                    testId="companies-empty"
                    action={
                      <EmptyAction
                        label="Add company"
                        allowed={isAdmin}
                        reason="An Admin adds companies."
                        onClick={() => setPanel("add")}
                        testId="companies-empty-add"
                      />
                    }
                  >
                    Every site and reporting period belongs to a company.
                  </EmptyState>
                ) : undefined
              }
              testId="companies-table"
            />
          ) : undefined
        }
        state={
          (entities.error ?? (window ? tree.error : null)) ? (
            <ReadFailed
              of="The companies"
              error={entities.error ?? tree.error}
              onRetry={() => {
                void entities.refetch();
                void tree.refetch();
              }}
            />
          ) : !entities.data ? (
            <GridTable
              caption="Companies"
              columns={[]}
              rows={[]}
              rowKey={(e: Entity) => e.id}
              loading
              testId="companies-loading"
            />
          ) : undefined
        }
      />
      <Drawer open={panel === "add"} onClose={closePanel} title="Add company" width="md">
        <EntityForm onSubmit={reporter.report} />
        {reporter.notes}
        {/* **The absence, stated.** An Admin who finds no way to create a workspace concludes there is
            none to find. A workspace and its first Admin are made by EmitIQ (ADR-0043, ADR-0046) and a
            colleague is invited on Admin; this note holds no control, because a button here would be
            the workspace-creating path ADR-0043 says has no membership to scope it. */}
        <BasisAndLimits testId="org-basis">
          <p data-testid="org-creating-workspace" data-references="ADR-0043 ADR-0046">
            A new <strong>workspace</strong> and its first Admin are created by EmitIQ, not from inside a
            workspace: that one act has no membership to be scoped by, so it is a separate step for
            EmitIQ, and the first Admin sets their own password from an invitation. Ask your EmitIQ
            contact if you need another.
          </p>
          <p>
            Inviting a colleague is done on <strong>Admin</strong>, under People and roles: it produces a
            single-use link for you to hand over.
          </p>
        </BasisAndLimits>
      </Drawer>
      <Drawer open={panel === "ownership"} onClose={closePanel} title="Ownership" width="lg">
        <GroupSection entities={companies} onSubmit={reporter.report} />
        {reporter.notes}
      </Drawer>
      <Drawer open={panel === "windows"} onClose={closePanel} title="Reporting periods" width="lg">
        <div className={styles.stack}>
          <PeriodList periods={periods} />
          <PeriodForm entities={companies} onSubmit={reporter.report} />
        </div>
        {reporter.notes}
      </Drawer>
      <Drawer
        open={corrected !== undefined}
        onClose={() => {
          setCorrecting(null);
          reporter.clear();
        }}
        title={corrected?.legal_name ?? "Company"}
        width="md"
      >
        {corrected ? (
          <EntityCorrection
            entityId={corrected.id}
            onSubmit={reporter.report}
            onDone={() => setCorrecting(null)}
          />
        ) : null}
        {reporter.notes}
      </Drawer>
    </>
  );
}
